import * as bootstrap from 'bootstrap';
import { get, post, notifier } from '../util';
import * as Highcharts from 'highcharts';
import { updateChartTotals, setActiveStepper, estimateStyleFor } from './shared';

// Module-level state
let modal: bootstrap.Modal | null = null;
let currentChartType: string = '';   // original id passed to openModal ('alc'|'weight'|'waist'|'gym')
let currentDate: string = '';
let isEdit: boolean = false;

/**
 * T-263 fix (Rule 1, 263-11): cindy-modal.ts is the first sibling modal to reuse this shared
 * #chart-edit-modal footer (`.js-modal-save`/`.js-modal-delete`) instead of hiding it outright —
 * gym/mood/runs/stairmaster all set `.modal-footer` to `d-none` while their own body content is
 * shown, so THIS module's saveBtn/deleteBtn click listeners (bound once, unconditionally, in
 * initModal() below) are physically unclickable for them. Without this guard, a stale
 * currentChartType left over from an earlier alc/weight/waist/weed/coffee/snap edit THIS SESSION
 * would let saveModal()/deleteModal() also fire on every Cindy save/delete click — at best a
 * wasted `?do=updateValue` round trip, at worst a silent write/delete against the WRONG type and
 * date, plus a premature `modal.hide()` (the shared bootstrap instance) mid-Cindy-save.
 * openModal() claims ownership by setting currentChartType to a real value; cindy-modal.ts's
 * openCindyModal() calls clearModalOwnership() before it reveals the shared footer, so
 * saveModal()/deleteModal() become inert (currentChartType === '', the same safe pre-first-open
 * default) until this module's own openModal() runs again.
 */
export function clearModalOwnership(): void {
    currentChartType = '';
}

const GYM_COLUMNS = ['chest', 'biceps', 'back', 'triceps', 'legs', 'shoulders', 'core', 'forearms', 'rock', 'cardio'] as const;
const GYM_LABELS  = ['Chest', 'Biceps', 'Back', 'Triceps', 'Legs', 'Shoulders', 'Core', 'Forearms', 'Rock Climbing', 'Cardio'] as const;

// Idempotency guard (feedback_shared_component_every_page_init_double_bind) — the
// shown.bs.modal (D-03 autofocus) + container keydown (D-04/D-05 Enter-to-save) listeners
// must be bound exactly once, mirroring ask.ts's askInitialised convention.
let modalListenersBound = false;

// WR-01 (220-REVIEW): ids of fields the user has typed into during the current open-cycle.
// Reset at the top of every openModal() call; populated via a single delegated `input`
// listener bound once on the stable #chart-edit-modal-body container (see
// renderModalSkeleton). A late getValuesFromDate response must never overwrite a field the
// user is actively editing.
let dirtyFields: Set<string> = new Set();
let bodyDirtyListenerBound = false;

/** WR-01: true when the user has TYPED into this field during the current open-cycle — the
 *  only signal that a late populate response would clobber real user input. Focus alone is
 *  deliberately NOT treated as dirty: the modal auto-focuses its first input on open, so a
 *  focus-based guard skipped populating that field's fetched value (empty-first-field bug,
 *  caught at the 226-09 deploy gate). dirtyFields is fed by the delegated `input` listener,
 *  so any real keystroke before the AJAX returns is still protected. */
function isFieldDirty(input: HTMLInputElement): boolean {
    return dirtyFields.has(input.id);
}

/** Id of the primary control to autofocus per form type (D-03). Gym/dead-branch omitted. */
function primaryInputId(formType: string): string | null {
    if (formType === 'snap') return 'modal-snap-score';
    if (formType === 'weight') return 'modal-weight';
    if (formType === 'alc') return 'modal-alc-qty';
    if (formType === 'coffee') return 'modal-coffee-qty';
    if (formType === 'weed') return 'modal-weed-puffs';
    return null;
}

export function initModal(): void {
    const modalEl = document.getElementById('chart-edit-modal');
    if (!modalEl) return;

    modal = new bootstrap.Modal(modalEl, { backdrop: true, keyboard: true });

    if (!modalListenersBound) {
        modalListenersBound = true;

        // IN-01 fix (220-REVIEW): saveBtn/deleteBtn used to sit OUTSIDE this guard while the
        // shown.bs.modal/keydown listeners below sat inside it — an inconsistent application
        // of the project's own documented double-bind guard within the same function
        // (feedback_shared_component_every_page_init_double_bind). Moved inside so a future
        // re-call of initModal() can never double-bind any of the four listeners.
        const saveBtn = modalEl.querySelector('.js-modal-save') as HTMLButtonElement;
        const deleteBtn = modalEl.querySelector('.js-modal-delete') as HTMLButtonElement;

        if (saveBtn) {
            saveBtn.addEventListener('click', () => saveModal());
        }
        if (deleteBtn) {
            deleteBtn.addEventListener('click', () => deleteModal());
        }

        // D-03: autofocus the primary control once the modal is actually shown (fires after
        // the skeleton is already rendered — see openModal's show-first ordering).
        modalEl.addEventListener('shown.bs.modal', () => {
            const formType = currentChartType === 'waist' ? 'weight' : currentChartType;
            const focusId = primaryInputId(formType);
            if (!focusId) return;
            const el = document.getElementById(focusId) as HTMLInputElement | null;
            el?.focus();
        });

        // D-04/D-05: container-level Enter-to-save. Runs the same code path as the Save
        // button (including its spinner + re-entrancy guard). Falls through to native
        // behavior for any focused <button> (e.g. a future per-card action) so it never
        // hijacks button-level Enter semantics.
        //
        // Self-select guard (Rule 1 fix, added by Phase 220 Plan 04): this listener lives on
        // the SAME shared #chart-edit-modal that gym/runs/stairmaster-modal.ts also render
        // into, and currentChartType is module-level state that is never reset on close (no
        // hidden.bs.modal listener exists in this file) — so without this guard, pressing
        // Enter inside a gym/runs/stairmaster field would ALSO fire this listener and call
        // saveModal() with a stale chart type + date, posting a spurious/empty updateValue
        // request (and, for a stale 'snap' type, surfacing a false "Score is required" inline
        // error inside whichever modal is actually open). The shared footer (containing
        // `.js-modal-save`) is only hidden by gym/runs/stairmaster while THEIR body content is
        // shown, so "footer visible" is a reliable self-select check that this listener owns
        // the current Enter press (mirrors Pattern 1's self-selecting design, gym/runs/
        // stairmaster's own new listeners self-select the same way via their own DOM markers).
        modalEl.addEventListener('keydown', (e: KeyboardEvent) => {
            if (e.key !== 'Enter') return;
            const target = e.target as HTMLElement;
            if (target.tagName === 'BUTTON') return;
            const footer = modalEl.querySelector('.modal-footer') as HTMLElement | null;
            if (!footer || footer.classList.contains('d-none')) return;
            e.preventDefault();
            saveModal();
        });
    }
}

/**
 * D-01 show-first skeleton: the real (enabled, empty) form markup for a given form type,
 * rendered synchronously BEFORE the getValuesFromDate round-trip so the modal frame is
 * usable near-instantly. Inputs are ENABLED (not disabled \u2014 RESEARCH Pattern 1: disabled
 * inputs cannot receive focus, and D-03's autofocus must land on a real, focusable input
 * from the very first paint). Gym is intentionally excluded (RESEARCH Pitfall 2 \u2014 dead
 * branch, home.ts routes 'gym' clicks to gym-modal.ts before openModal() is ever reached;
 * left rendering exactly as it always has, inside the AJAX callback below).
 */
function renderModalSkeleton(formType: string): void {
    const body = document.getElementById('chart-edit-modal-body');
    if (!body) return;
    body.innerHTML = '';

    if (!bodyDirtyListenerBound) {
        bodyDirtyListenerBound = true;
        // WR-01: delegated once on the stable #chart-edit-modal-body container — innerHTML
        // rebuilds only its children (never the container itself), so this listener survives
        // every renderModalSkeleton() re-render across every open-cycle.
        body.addEventListener('input', (e: Event) => {
            const target = e.target as HTMLElement;
            if (target?.id) dirtyFields.add(target.id);
        });
    }

    if (formType === 'alc') {
        body.innerHTML = `
            <div class="mb-3">
                <div class="d-flex align-items-end gap-3">
                    <div>
                        <label class="form-label">Quantity</label>
                        <input type="number" class="form-control" id="modal-alc-qty" step="any" inputmode="decimal" value="" />
                    </div>
                    <div class="form-check form-check-inline mb-2">
                        <input class="form-check-input" type="checkbox" id="modal-alc-estimate" />
                        <label class="form-check-label" for="modal-alc-estimate">Estimate</label>
                    </div>
                </div>
            </div>
        `;
    } else if (formType === 'coffee') {
        body.innerHTML = `
            <div class="mb-3">
                <div class="d-flex align-items-end gap-3">
                    <div>
                        <label class="form-label">Quantity</label>
                        <input type="number" class="form-control" id="modal-coffee-qty" step="1" inputmode="numeric" value="" />
                    </div>
                    <div class="form-check form-check-inline mb-2">
                        <input class="form-check-input" type="checkbox" id="modal-coffee-estimate" />
                        <label class="form-check-label" for="modal-coffee-estimate">Estimate</label>
                    </div>
                </div>
            </div>
        `;
    } else if (formType === 'weight') {
        body.innerHTML = `
            <div class="row g-2">
                <div class="col-6">
                    <label class="form-label">Weight (lbs)</label>
                    <input type="number" class="form-control" id="modal-weight" step="any" inputmode="decimal" value="" />
                </div>
                <div class="col-6">
                    <label class="form-label">Waist (cm)</label>
                    <input type="number" class="form-control" id="modal-waist" step="any" inputmode="decimal" value="" />
                </div>
            </div>
        `;
    } else if (formType === 'weed') {
        body.innerHTML = `
            <div class="row g-2">
                <div class="col-6">
                    <label class="form-label">Puffs</label>
                    <input type="number" class="form-control" id="modal-weed-puffs" step="1" inputmode="numeric" value="" />
                </div>
                <div class="col-6">
                    <label class="form-label">THC Drinks</label>
                    <input type="number" class="form-control" id="modal-weed-thc" step="1" inputmode="numeric" value="" />
                </div>
            </div>
            <div class="form-check form-check-inline mt-2">
                <input class="form-check-input" type="checkbox" id="modal-weed-estimate" />
                <label class="form-check-label" for="modal-weed-estimate">Estimate</label>
            </div>
        `;
    } else if (formType === 'snap') {
        body.innerHTML = `
            <div class="mb-3">
                <div class="d-flex align-items-end gap-3">
                    <div>
                        <label class="form-label">Score</label>
                        <input type="number" class="form-control" id="modal-snap-score" step="1" inputmode="numeric" value="" />
                    </div>
                    <div class="form-check form-check-inline mb-2">
                        <input class="form-check-input" type="checkbox" id="modal-snap-est" />
                        <label class="form-check-label" for="modal-snap-est">Estimate</label>
                    </div>
                </div>
            </div>
        `;
    }
    // formType === 'gym' deliberately falls through with an empty body \u2014 see doc comment.
}

/**
 * Populate the already-rendered skeleton's fields once getValuesFromDate resolves. Sets
 * `.value` on existing DOM nodes (never rebuilds body.innerHTML here) so a shown.bs.modal
 * focus that already landed on a skeleton input is never destroyed by a late AJAX response.
 *
 * D-06/D-07 ghost placeholder: add-mode only, from `data.last.<metric>` when non-null;
 * edit-mode sets the real stored value and leaves placeholder untouched (absent/empty).
 * The empty-save-writes-nothing guarantee is server-side authoritative (220-02 D-07).
 */
function populateModalValues(formType: string, data: any, editMode: boolean): void {
    if (formType === 'alc') {
        const qtyInput = document.getElementById('modal-alc-qty') as HTMLInputElement | null;
        const estimateCheckbox = document.getElementById('modal-alc-estimate') as HTMLInputElement | null;
        if (qtyInput) {
            if (editMode) {
                // WR-01: skip the overwrite if the user is already editing this field.
                if (!isFieldDirty(qtyInput)) qtyInput.value = data.alc ?? '';
            } else if (data.last?.alc !== null && data.last?.alc !== undefined) {
                qtyInput.placeholder = String(data.last.alc);
            }
        }
        if (estimateCheckbox) estimateCheckbox.checked = editMode && !!data.alc_estimate;
    } else if (formType === 'coffee') {
        const qtyInput = document.getElementById('modal-coffee-qty') as HTMLInputElement | null;
        const estimateCheckbox = document.getElementById('modal-coffee-estimate') as HTMLInputElement | null;
        if (qtyInput) {
            if (editMode) {
                // WR-01: skip the overwrite if the user is already editing this field.
                if (!isFieldDirty(qtyInput)) qtyInput.value = data.coffee ?? '';
            } else if (data.last?.coffee !== null && data.last?.coffee !== undefined) {
                qtyInput.placeholder = String(data.last.coffee);
            }
        }
        if (estimateCheckbox) estimateCheckbox.checked = editMode && !!data.coffee_estimate;
    } else if (formType === 'weight') {
        const weightInput = document.getElementById('modal-weight') as HTMLInputElement | null;
        const waistInput = document.getElementById('modal-waist') as HTMLInputElement | null;
        if (weightInput) {
            if (editMode) {
                // WR-01: skip the overwrite if the user is already editing this field.
                if (!isFieldDirty(weightInput)) weightInput.value = data.weight ?? '';
            } else if (data.last?.weight !== null && data.last?.weight !== undefined) {
                weightInput.placeholder = String(data.last.weight);
            }
        }
        if (waistInput) {
            if (editMode) {
                // WR-01: skip the overwrite if the user is already editing this field.
                if (!isFieldDirty(waistInput)) waistInput.value = data.waist ?? '';
            } else if (data.last?.waist !== null && data.last?.waist !== undefined) {
                waistInput.placeholder = String(data.last.waist);
            }
        }
    } else if (formType === 'weed') {
        const puffsInput = document.getElementById('modal-weed-puffs') as HTMLInputElement | null;
        const thcInput = document.getElementById('modal-weed-thc') as HTMLInputElement | null;
        const estimateCheckbox = document.getElementById('modal-weed-estimate') as HTMLInputElement | null;
        if (puffsInput) {
            if (editMode) {
                // WR-01: skip the overwrite if the user is already editing this field.
                if (!isFieldDirty(puffsInput)) puffsInput.value = data.weed_puffs ?? '';
            } else if (data.last?.weed_puffs !== null && data.last?.weed_puffs !== undefined) {
                puffsInput.placeholder = String(data.last.weed_puffs);
            }
        }
        if (thcInput) {
            if (editMode) {
                // WR-01: skip the overwrite if the user is already editing this field.
                if (!isFieldDirty(thcInput)) thcInput.value = data.weed_thc ?? '';
            } else if (data.last?.weed_thc !== null && data.last?.weed_thc !== undefined) {
                thcInput.placeholder = String(data.last.weed_thc);
            }
        }
        if (estimateCheckbox) estimateCheckbox.checked = editMode && !!data.weed_estimate;
    } else if (formType === 'snap') {
        const scoreInput = document.getElementById('modal-snap-score') as HTMLInputElement | null;
        const estCheckbox = document.getElementById('modal-snap-est') as HTMLInputElement | null;
        if (scoreInput) {
            if (editMode) {
                // WR-01: skip the overwrite if the user is already editing this field.
                if (!isFieldDirty(scoreInput)) scoreInput.value = data.snap ?? '';
            } else if (data.last?.snap !== null && data.last?.snap !== undefined) {
                scoreInput.placeholder = String(data.last.snap);
            }
        }
        if (estCheckbox) estCheckbox.checked = editMode && !!data.snap_est;
    }
}

/** Bare type label for the modal title (P-01: date-free) — the single source both the
 *  show-first placeholder and the post-fetch resolved title read from, so they can never
 *  drift apart on wording. */
function typeLabelFor(formType: string): string {
    return formType === 'alc' ? 'Alcohol' : formType === 'coffee' ? 'Coffee' : formType === 'weight' ? 'Weight & Waist' : formType === 'weed' ? 'Weed' : formType === 'snap' ? 'Snap Score' : 'Gym';
}

/**
 * Fetch-and-populate half of openModal() (Phase 262, HOME-01/02) — extracted so the date-nav
 * step path can re-run it against a new date without closing/reopening the modal. Runs the
 * existing getValuesFromDate round trip, resolves edit-vs-add mode exactly as before, writes
 * the date-free (P-01) title, toggles the delete button, and populates the already-rendered
 * skeleton (D-06/D-07 ghost placeholder).
 *
 * Only renders the skeleton itself when the body is currently EMPTY, i.e. only when reached
 * via a step, which blanks the body outright before calling this (P-04). On the open path the
 * show-first skeleton (rendered synchronously by openModal(), before this ever runs) is left
 * alone, so a value typed during this very round trip stays protected by
 * populateModalValues()'s WR-01 dirty-field check instead of being wiped by a second, redundant
 * rebuild -- DEBT-04 IN-01's open-path behaviour must not regress.
 */
function reloadModalBody(date: string, formType: string, done?: (ok: boolean) => void): void {
    get('?do=getValuesFromDate&date=' + date, {}, (data: any) => {
        if (!data) { done?.(false); return; }   // T-262-33: guard against a malformed response

        // CR-01 stale-response guard (mirrors mood-modal.ts:207). openModal()'s own fetch is NOT
        // covered by date-nav's _busy lock -- that lock only serialises stepTo() against itself --
        // so opening the modal and immediately stepping leaves two getValuesFromDate requests in
        // flight for two different dates, and whichever lands last wins. Without this check an
        // older response repopulates the form while currentDate and the header still show the
        // newer date, and a subsequent save writes the old date's values onto the new date's row.
        // Compare against the closure-captured `date`: step()/openModal() both assign currentDate
        // synchronously before issuing the fetch (the file's T-262-30 convention), so this needs
        // no echoed-back date field from the server.
        if (date !== currentDate) { done?.(true); return; }

        const modalEl = document.getElementById('chart-edit-modal');
        const modalTitle = document.getElementById('chart-edit-modal-title');
        const deleteBtn = modalEl?.querySelector('.js-modal-delete') as HTMLButtonElement | null;

        // Determine edit vs add mode based on chart type
        let hasData = false;
        if (formType === 'alc') {
            hasData = data.alc !== null && data.alc !== undefined;
        } else if (formType === 'coffee') {
            hasData = data.coffee !== null && data.coffee !== undefined;
        } else if (formType === 'weight') {
            hasData = data.weight !== null || data.waist !== null;
        } else if (formType === 'weed') {
            hasData = data.weed_puffs !== null && data.weed_puffs !== undefined;
        } else if (formType === 'snap') {
            hasData = data.snap !== null && data.snap !== undefined;
        }
        isEdit = hasData;

        // P-01: the title is date-free, always -- set once here for both open and step.
        if (modalTitle) {
            modalTitle.textContent = (isEdit ? 'Edit ' : 'Add ') + typeLabelFor(formType);
        }

        // Show/hide delete button
        if (deleteBtn) {
            deleteBtn.classList.toggle('d-none', !isEdit);
        }

        // DEBT-04 IN-01 (223-REVIEW): the dead formType === 'gym' render branch that used to
        // sit here (full grid render + a stale read off the response's `gym` field) has been
        // removed -- getValuesFromDate() no longer returns a `gym` field (223 cleanup) and this
        // branch was already unreachable from the live UI (home.ts intercepts 'gym' clicks
        // before openModal() is ever called with 'gym'; gym editing owns gym-modal.ts
        // exclusively).

        const body = document.getElementById('chart-edit-modal-body');
        if (body && body.children.length === 0) {
            // Reached via a step (P-04 blanked the body outright before calling this) -- the
            // open path's show-first skeleton is already up by the time this callback runs.
            renderModalSkeleton(formType);
        }

        // Populate the already-rendered skeleton in place (D-06/D-07 ghost placeholder).
        populateModalValues(formType, data, isEdit);
        done?.(true);
    });
}

/**
 * Date-nav step callback (Phase 262, HOME-01/02) -- the ModalDateStepper handed to shared.ts's
 * active-stepper registry from inside openModal().
 */
function step(newDate: string, done: (ok: boolean) => void): void {
    // T-262-30: currentDate is reassigned BEFORE the fetch is issued, never inside the
    // response callback -- a step is a new route to the exact stale-state failure mode this
    // module's Enter-to-save self-select guard (initModal(), above) already had to account for.
    currentDate = newDate;
    // P-02: stepping the date is navigation, not a merge -- unsaved edits are discarded
    // silently. No confirm dialog, no auto-save before stepping.
    dirtyFields.clear();

    // P-04: blank the body outright -- no skeleton, no spinner. The values on screen must
    // never be a mix of two dates.
    const body = document.getElementById('chart-edit-modal-body');
    if (body) body.innerHTML = '';

    const formType = currentChartType === 'waist' ? 'weight' : currentChartType;
    reloadModalBody(newDate, formType, done);
}

export function openModal(chartType: string, date: string): void {
    if (!modal) return;

    currentChartType = chartType;
    currentDate = date;
    dirtyFields.clear();   // WR-01: fresh open-cycle, nothing typed yet.

    // Determine form type: waist maps to weight form
    const formType = chartType === 'waist' ? 'weight' : chartType;

    // Phase 262 HOME-01/02: register this modal's step implementation with the shared
    // date-nav control before the modal is shown, so its header sync paints the clicked date
    // the instant the control appears.
    setActiveStepper(date, step);

    const modalEl = document.getElementById('chart-edit-modal')!;
    const modalTitle = document.getElementById('chart-edit-modal-title');
    const deleteBtn = modalEl.querySelector('.js-modal-delete') as HTMLButtonElement;
    // WR-04 (220-REVIEW): proactively re-show the shared footer rather than relying solely
    // on gym/runs/stairmaster's own hidden.bs.modal cleanup — a single point of failure that
    // would otherwise leave this modal's Save/Delete buttons permanently hidden if that event
    // is ever skipped.
    const footer = modalEl.querySelector('.modal-footer') as HTMLElement | null;
    footer?.classList.remove('d-none');

    // D-01: show-first. Render the real skeleton + show the modal BEFORE the AJAX
    // round-trip. Gym is excluded (dead branch \u2014 see renderModalSkeleton doc comment);
    // it keeps its original show()-inside-callback behavior, untouched.
    if (formType !== 'gym') {
        renderModalSkeleton(formType);
        // P-01: date-free placeholder — corrected to the resolved Add/Edit label the
        // instant reloadModalBody's fetch resolves below.
        if (modalTitle) modalTitle.textContent = 'Edit ' + typeLabelFor(formType);
        if (deleteBtn) deleteBtn.classList.add('d-none');
        modal!.show();
    }

    reloadModalBody(date, formType);
}

export function saveModal(): void {
    if (!modal) return;
    if (!currentChartType) return;   // T-263 fix — see clearModalOwnership() doc comment above

    const modalEl = document.getElementById('chart-edit-modal')!;
    const saveBtn = modalEl.querySelector('.js-modal-save') as HTMLButtonElement;
    if (saveBtn.disabled) return;   // D-04 re-entrancy guard — Enter-to-save can call this
                                     // directly, bypassing native disabled-button click suppression

    const saveLabel = modalEl.querySelector('.js-modal-save-label') as HTMLElement;
    const spinner = modalEl.querySelector('.js-modal-spinner') as HTMLElement;

    // Disable button and show spinner
    saveBtn.disabled = true;
    saveLabel.classList.add('d-none');
    spinner.classList.remove('d-none');

    // Remove any previous inline error
    const prevError = modalEl.querySelector('.modal-inline-error');
    if (prevError) prevError.remove();

    const formType = currentChartType === 'waist' ? 'weight' : currentChartType;
    let fields: Record<string, string | number> = {};

    if (formType === 'alc') {
        const input = document.getElementById('modal-alc-qty') as HTMLInputElement;
        const estimateCheckbox = document.getElementById('modal-alc-estimate') as HTMLInputElement;
        fields = { qty: input ? input.value : '', estimate: estimateCheckbox && estimateCheckbox.checked ? 1 : 0 };
    } else if (formType === 'coffee') {
        const input = document.getElementById('modal-coffee-qty') as HTMLInputElement;
        const estimateCheckbox = document.getElementById('modal-coffee-estimate') as HTMLInputElement;
        fields = { qty: input ? input.value : '', estimate: estimateCheckbox && estimateCheckbox.checked ? 1 : 0 };
    } else if (formType === 'weight') {
        const wInput = document.getElementById('modal-weight') as HTMLInputElement;
        const waistInput = document.getElementById('modal-waist') as HTMLInputElement;
        fields = {
            weight: wInput ? wInput.value : '',
            waist: waistInput ? waistInput.value : ''
        };
    } else if (formType === 'weed') {
        const puffsInput = document.getElementById('modal-weed-puffs') as HTMLInputElement;
        const thcInput = document.getElementById('modal-weed-thc') as HTMLInputElement;
        const estimateCheckbox = document.getElementById('modal-weed-estimate') as HTMLInputElement;
        fields = {
            puffs: puffsInput ? puffsInput.value : '',
            thc_drinks: thcInput ? thcInput.value : '',
            estimate: estimateCheckbox && estimateCheckbox.checked ? 1 : 0
        };
    } else if (formType === 'gym') {
        GYM_COLUMNS.forEach(col => {
            const cb = document.querySelector(`#modal-gym-${col}`) as HTMLInputElement;
            fields[col] = cb && cb.checked ? 1 : 0;
        });
    } else if (formType === 'snap') {
        const scoreInput = document.getElementById('modal-snap-score') as HTMLInputElement;
        const estCheckbox = document.getElementById('modal-snap-est') as HTMLInputElement;
        fields = { score: scoreInput ? scoreInput.value : '', est: estCheckbox && estCheckbox.checked ? 1 : 0 };
    }

    post('?do=updateValue&type=' + formType + '&date=' + currentDate, fields, (data: any) => {
        // Always re-enable save button
        saveBtn.disabled = false;
        saveLabel.classList.remove('d-none');
        spinner.classList.add('d-none');

        if (data.status === 1) {
            // D-07 (CR-01): an empty-field save is a server no-op (nothing written). Do NOT
            // paint a blank/NaN chart point or show a false "Saved" toast — just close,
            // leaving the chart point and titlebar total exactly as they were.
            if (data.noop) {
                modal!.hide();
                return;
            }
            updateChartPoint(formType, fields, data.bmi);
            // D-09/D-10: server-authoritative titlebar swap from the save response's
            // range-scoped totals — no reload. Weight/waist is the sole nested exception
            // (one form, two titlebars); alc (and the dead gym branch) get the flat swap.
            // Snap is excluded here: updateChartPoint('snap', ...) above already triggered
            // refreshSnapChart()'s own full re-fetch + updateSnapTotals() — the existing
            // MODAL-05 reference pattern, kept as-is (not duplicated via this helper).
            if (formType === 'weight') {
                updateChartTotals('weight', data.totals?.weight);
                updateChartTotals('waist', data.totals?.waist);
                // P-07: the bmi titlebar updates in the SAME branch, from the SAME response,
                // as the point update above — a chart whose line moves while its own Current
                // total still shows the previous number would read as a bug (T-262-45).
                if (data.bmi_totals) updateChartTotals('bmi', data.bmi_totals);
                // UI-SPEC Component Specification 4: editing weight/waist visibly changes a
                // DIFFERENT card (bmi) that may be off-screen (especially single-column
                // mobile) with no direct user action there — flash it via the existing
                // drag-drop keyframe verbatim (style.scss chart-drop-flash, 0.6s), zero new
                // CSS. Guarded (the graph may be hidden/not rendered) and purely a polish
                // signal — never gates the point/totals update above, which is what actually
                // satisfies HOME-04.
                if (data.bmi !== undefined) {
                    const bmiWrapper = document.querySelector('.chart-wrapper[data-id="bmi"]');
                    if (bmiWrapper) {
                        bmiWrapper.classList.add('chart-wrapper--dropped');
                        window.setTimeout(() => bmiWrapper.classList.remove('chart-wrapper--dropped'), 600);
                    }
                }
            } else if (formType === 'weed') {
                // D-01/D-03: weed is ONE home graph = ONE chart-wrapper (unlike weight/
                // waist's two separate wrappers), so its nested {puffs:[...], thc_drinks:
                // [...]} totals payload (mirrors getWeightWaistTotals()'s nested-return
                // shape, per 222-02) must be flattened into a single list before reaching
                // the single data-id="weed" wrapper's flat .chart-total loop.
                const weedTotals = [...(data.totals?.puffs ?? []), ...(data.totals?.thc_drinks ?? [])];
                updateChartTotals('weed', weedTotals);
            } else if (formType !== 'snap') {
                updateChartTotals(formType, data.totals);
            }
            modal!.hide();
            notifier.success('Saved');
        } else {
            // Show inline error
            const errorEl = document.createElement('div');
            errorEl.className = 'text-danger small mt-2 modal-inline-error';
            errorEl.textContent = data.message || 'An error occurred. Please try again.';
            const body = document.getElementById('chart-edit-modal-body');
            if (body) body.appendChild(errorEl);
        }
    });
}

export function deleteModal(): void {
    if (!modal) return;
    if (!currentChartType) return;   // T-263 fix — see clearModalOwnership() doc comment above

    if (!window.confirm('Delete this entry?')) return;

    const modalEl = document.getElementById('chart-edit-modal')!;
    const deleteBtn = modalEl.querySelector('.js-modal-delete') as HTMLButtonElement;
    deleteBtn.disabled = true;

    // Remove any previous inline error
    const prevError = modalEl.querySelector('.modal-inline-error');
    if (prevError) prevError.remove();

    // IN-02 fix (220-REVIEW, data-loss): unlike openModal()/saveModal() — whose combined
    // weight+waist FORM always remaps waist -> weight because both fields are edited
    // together in the shared modal body — a delete is a destructive per-datum action tied
    // to whichever chart the user actually clicked. Sending the real currentChartType here
    // (never remapped) lets the 226-03 backend's dedicated `waist` deleteValue branch clear
    // ONLY the waist reading; the old remap silently wiped the weight reading too whenever a
    // delete was triggered from the waist chart.
    const formType = currentChartType;

    post('?do=deleteValue&type=' + formType + '&date=' + currentDate, {}, (data: any) => {
        deleteBtn.disabled = false;

        if (data.status === 1) {
            nullChartPoint(formType);
            // WARNING-1/D-10: the delete response's range-scoped totals are consumed the
            // same way the save path consumes them — otherwise this titlebar refresh is
            // dead output and the chart goes stale after a delete. `waist` shares weight's
            // nested {weight,waist} totals payload (getWeightWaistTotals() always returns
            // both) even though only the waist datum was cleared server-side — both chips
            // are safe (and correct) to re-paint from the fresh response.
            if (formType === 'weight' || formType === 'waist') {
                updateChartTotals('weight', data.totals?.weight);
                updateChartTotals('waist', data.totals?.waist);
            } else if (formType === 'weed') {
                // See saveModal()'s matching comment — weed's nested totals must be
                // flattened before reaching the single data-id="weed" wrapper.
                const weedTotals = [...(data.totals?.puffs ?? []), ...(data.totals?.thc_drinks ?? [])];
                updateChartTotals('weed', weedTotals);
            } else {
                updateChartTotals(formType, data.totals);
            }
            modal!.hide();
            notifier.success('Deleted');
        } else {
            const errorEl = document.createElement('div');
            errorEl.className = 'text-danger small mt-2 modal-inline-error';
            errorEl.textContent = data.message || 'An error occurred. Please try again.';
            const body = document.getElementById('chart-edit-modal-body');
            if (body) body.appendChild(errorEl);
        }
    });
}

function getPointIndex(series: Highcharts.Series, date: string): number {
    //@ts-ignore
    const pointStart = series.options.pointStart as number;
    // Use local time to match pointStart which is Date.parse(dates_array[0]) (local)
    const clickedTimestamp = Date.parse(date);
    return Math.round((clickedTimestamp - pointStart) / 86400000);
}

function findChart(chartId: string): Highcharts.Chart | undefined {
    //@ts-ignore
    return Highcharts.charts.find((c: any) => c?.renderTo?.id === chartId);
}

function updateChartPoint(formType: string, fields: Record<string, string | number>, bmi?: number | null): void {
    if (formType === 'snap') { refreshSnapChart(); return; }
    if (formType === 'alc') {
        const chart = findChart('chart_alc');
        if (!chart) return;
        const series = chart.series[0];
        const idx = getPointIndex(series, currentDate);
        const point = series.data[idx];
        if (point !== undefined) {
            const isEstimate = fields.estimate === 1;
            //@ts-ignore
            point.update({
                y: parseFloat(fields.qty as string) * -1,
                color: isEstimate ? estimateStyleFor(formType) : series.color,
                borderWidth: isEstimate ? 1 : 0,
                dashStyle: isEstimate ? 'ShortDash' : 'Solid',
                custom: { estimate: isEstimate ? 1 : 0 },
            });
        }
    } else if (formType === 'coffee') {
        const chart = findChart('chart_coffee');
        if (!chart) return;
        const series = chart.series[0];
        const idx = getPointIndex(series, currentDate);
        const point = series.data[idx];
        if (point !== undefined) {
            const isEstimate = fields.estimate === 1;
            //@ts-ignore
            point.update({
                y: parseFloat(fields.qty as string),
                color: isEstimate ? estimateStyleFor(formType) : series.color,
                borderWidth: isEstimate ? 1 : 0,
                dashStyle: isEstimate ? 'ShortDash' : 'Solid',
                custom: { estimate: isEstimate ? 1 : 0 },
            });
        }
    } else if (formType === 'weight') {
        // Update weight chart
        const weightChart = findChart('chart_weight');
        if (weightChart) {
            const series = weightChart.series[0];
            const idx = getPointIndex(series, currentDate);
            const point = series.data[idx];
            if (point !== undefined) {
                //@ts-ignore
                point.update(parseFloat(fields.weight as string) || null);
            }
        }
        // Update waist chart
        const waistChart = findChart('chart_waist');
        if (waistChart) {
            const series = waistChart.series[0];
            const idx = getPointIndex(series, currentDate);
            const point = series.data[idx];
            if (point !== undefined) {
                //@ts-ignore
                point.update(parseFloat(fields.waist as string) || null);
            }
        }
        // Update bmi chart (P-06): the client never computes BMI — PHP's computeBmi() is the
        // single formula definition (T-262-40). `bmi` is checked for `undefined` (not just
        // falsy) so an older cached bundle or the noop path (neither carries `bmi` at all)
        // skips this block entirely rather than reaching point.update() with `undefined`,
        // which is a known Highcharts failure mode (T-262-44) in this codebase. An explicit
        // `null` (the cleared-weight case) is passed straight through — `connectNulls: true`
        // on the bmi series (home.ts) makes that a safe gap, never a spurious 0 inside the
        // WHO underweight band.
        if (bmi !== undefined) {
            const bmiChart = findChart('chart_bmi');
            if (bmiChart) {
                const series = bmiChart.series[0];
                const idx = getPointIndex(series, currentDate);
                const point = series.data[idx];
                if (point !== undefined) {
                    //@ts-ignore
                    point.update(bmi);
                }
            }
        }
    } else if (formType === 'gym') {
        const chart = findChart('chart_gym');
        if (!chart) return;
        GYM_COLUMNS.forEach((col, i) => {
            const series = chart.series[i];
            if (!series) return;
            const idx = getPointIndex(series, currentDate);
            const point = series.data[idx];
            if (point !== undefined) {
                //@ts-ignore
                point.update(fields[col] ? 1 : null);
            }
        });
    } else if (formType === 'weed') {
        const chart = findChart('chart_weed');
        if (!chart) return;
        const isEstimate = fields.estimate === 1;
        (['puffs', 'thc_drinks'] as const).forEach((col, i) => {
            const series = chart.series[i];
            if (!series) return;
            const idx = getPointIndex(series, currentDate);
            const point = series.data[idx];
            if (point !== undefined) {
                //@ts-ignore
                point.update({
                    y: parseFloat(fields[col] as string),
                    color: isEstimate ? estimateStyleFor(formType, i) : series.color,
                    borderWidth: isEstimate ? 1 : 0,
                    dashStyle: isEstimate ? 'ShortDash' : 'Solid',
                    custom: { estimate: isEstimate ? 1 : 0 },
                });
            }
        });
    }
}

function nullChartPoint(formType: string): void {
    if (formType === 'snap') { refreshSnapChart(); return; }
    if (formType === 'alc') {
        const chart = findChart('chart_alc');
        if (!chart) return;
        const series = chart.series[0];
        const idx = getPointIndex(series, currentDate);
        const point = series.data[idx];
        //@ts-ignore
        if (point !== undefined) point.update(null);
    } else if (formType === 'coffee') {
        const chart = findChart('chart_coffee');
        if (!chart) return;
        const series = chart.series[0];
        const idx = getPointIndex(series, currentDate);
        const point = series.data[idx];
        //@ts-ignore
        if (point !== undefined) point.update(null);
    } else if (formType === 'weight') {
        ['chart_weight', 'chart_waist'].forEach(chartId => {
            const chart = findChart(chartId);
            if (!chart) return;
            const series = chart.series[0];
            const idx = getPointIndex(series, currentDate);
            const point = series.data[idx];
            //@ts-ignore
            if (point !== undefined) point.update(null);
        });
    } else if (formType === 'waist') {
        // IN-02 fix: a waist-only delete (226-03's dedicated backend branch) must null
        // ONLY the waist chart point — the weight reading for this date survives, so
        // chart_weight must never be touched here (unlike the 'weight' branch above, which
        // deletes the whole row and nulls both).
        const chart = findChart('chart_waist');
        if (!chart) return;
        const series = chart.series[0];
        const idx = getPointIndex(series, currentDate);
        const point = series.data[idx];
        //@ts-ignore
        if (point !== undefined) point.update(null);
    } else if (formType === 'gym') {
        const chart = findChart('chart_gym');
        if (!chart) return;
        chart.series.forEach(series => {
            const idx = getPointIndex(series, currentDate);
            const point = series.data[idx];
            //@ts-ignore
            if (point !== undefined) point.update(null);
        });
    } else if (formType === 'weed') {
        const chart = findChart('chart_weed');
        if (!chart) return;
        // Only the two CURRENT-year series (puffs, thc_drinks) — never a blanket
        // forEach like gym's arm above. When the YoY toggle is on, home.ts appends
        // two prior-year overlay series to this same chart, so a forEach would also
        // null last year's bar for this date. Prior series are always appended after
        // the current pair, so indices 0 and 1 are the current year.
        chart.series.slice(0, 2).forEach(series => {
            const idx = getPointIndex(series, currentDate);
            const point = series.data[idx];
            //@ts-ignore
            if (point !== undefined) point.update(null);
        });
    }
}

/**
 * Refresh the Snapchat chart after a snap add/edit/delete — no page reload.
 *
 * Gap interpolation makes the series interdependent across an entire gap (changing one
 * reading re-interpolates every estimate between it and its neighbours), so instead of
 * patching points client-side we re-fetch the authoritative gap-interpolated series from
 * the server (?do=getSnapSeries) and swap both series' data in place. Single source of truth.
 */
function refreshSnapChart(): void {
    const chart = findChart('chart_snap');
    if (!chart) return;
    post('?do=getSnapSeries', {}, (resp: any) => {
        if (!resp || resp.success !== true || !resp.series || !resp.series.data) return;
        const data = resp.series.data;
        //@ts-ignore
        if (chart.series[0] && data[0]) chart.series[0].setData(data[0], false);
        //@ts-ignore
        if (chart.series[1] && data[1]) chart.series[1].setData(data[1], false);
        chart.redraw();
        updateSnapTotals(resp.series.totals);
    });
}

/**
 * Update the Score / Gained totals above the snap chart from a fresh server payload.
 *
 * WR-05 (220-REVIEW): `{ifset $value['totals']}` renders zero `.chart-total` nodes at page
 * load when `getSnapSeriesData()` returned `totals: []` for a fully-empty range (unlike alc/
 * weight/waist/km/stairmaster/gym, whose totals always return at least one COALESCE(...,0)
 * row). When that's the swap-target set, the match-by-label loop below has nothing to update
 * — so the very first snap save into such a range must instead build the same `.chart-total`
 * markup the server would have rendered (mirrors default.latte:99-103 / customize.ts's
 * buildChartWrapper()) from this fresh non-empty payload, inserted before the hide button so
 * position matches a server-rendered wrapper exactly.
 */
function updateSnapTotals(totals: Array<{ name: string; value: string }> | undefined): void {
    const wrapper = document.querySelector('.chart-wrapper[data-id="snap"]');
    if (!wrapper || !totals) return;
    const existing = wrapper.querySelectorAll('.chart-total');
    if (existing.length === 0) {
        if (totals.length === 0) return;   // nothing to build yet either
        const handle = wrapper.querySelector('.chart-drag-handle');
        if (!handle) return;
        const hideBtn = handle.querySelector('.chart-hide-btn');
        totals.forEach((total) => {
            const totalEl = document.createElement('div');
            totalEl.className = 'chart-total';
            const label = document.createElement('span');
            label.className = 'chart-total-label';
            label.textContent = total.name + ':';
            const value = document.createElement('span');
            value.className = 'chart-total-value';
            value.textContent = total.value;
            totalEl.appendChild(label);
            totalEl.appendChild(document.createTextNode(' '));
            totalEl.appendChild(value);
            if (hideBtn) {
                handle.insertBefore(totalEl, hideBtn);
            } else {
                handle.appendChild(totalEl);
            }
        });
        return;
    }
    existing.forEach((t) => {
        const label = t.querySelector('.chart-total-label')?.textContent?.trim().replace(/:$/, '');
        const valEl = t.querySelector('.chart-total-value');
        if (!valEl) return;
        const match = totals.find((x) => x.name === label);
        if (match) valEl.textContent = match.value;
    });
}
